import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";

// Lazy load non-critical routes
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Apply = lazy(() => import("./pages/Apply"));
const ApplySuccess = lazy(() => import("./pages/ApplySuccess"));
const Episodes = lazy(() => import("./pages/Episodes"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TexasEntrepreneurPodcast = lazy(() => import("./pages/TexasEntrepreneurPodcast"));
const EpisodeDetail = lazy(() => import("./pages/EpisodeDetail"));
const Press = lazy(() => import("./pages/Press"));
const Checkin = lazy(() => import("./pages/Checkin"));
const GuestPortal = lazy(() => import("./pages/GuestPortal"));
const GuestSignIn = lazy(() => import("./pages/GuestSignIn"));
const GuestReset = lazy(() => import("./pages/GuestReset"));
const SharePack = lazy(() => import("./pages/SharePack"));
// Topic Landing Pages
const MarketingTips = lazy(() => import("./pages/topics/MarketingTips"));
const ScalingBusiness = lazy(() => import("./pages/topics/ScalingBusiness"));
const HiringAdvice = lazy(() => import("./pages/topics/HiringAdvice"));
const HowToStartABusiness = lazy(() => import("./pages/HowToStartABusiness"));
// City Landing Pages
const HoustonEntrepreneurPodcast = lazy(() => import("./pages/cities/HoustonEntrepreneurPodcast"));
const DallasEntrepreneurPodcast = lazy(() => import("./pages/cities/DallasEntrepreneurPodcast"));
const BestPodcastInDallas = lazy(() => import("./pages/BestPodcastInDallas"));
const AustinEntrepreneurPodcast = lazy(() => import("./pages/cities/AustinEntrepreneurPodcast"));
const SanAntonioEntrepreneurPodcast = lazy(() => import("./pages/cities/SanAntonioEntrepreneurPodcast"));
// Admin Routes
const AdminAuth = lazy(() => import("./pages/AdminAuth"));
const AdminAcceptInvitation = lazy(() => import("./pages/AdminAcceptInvitation"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminPosts = lazy(() => import("./pages/admin/AdminPosts"));
const AdminPostEditor = lazy(() => import("./pages/admin/AdminPostEditor"));
const AdminAIStudio = lazy(() => import("./pages/admin/AdminAIStudio"));
const AdminContentCalendar = lazy(() => import("./pages/admin/AdminContentCalendar"));
const AdminTeam = lazy(() => import("./pages/admin/AdminTeam"));
const AdminContacts = lazy(() => import("./pages/admin/AdminContacts"));
const AdminBookings = lazy(() => import("./pages/admin/AdminBookings"));
const AdminInterview = lazy(() => import("./pages/admin/AdminInterview"));
const AdminMFASetup = lazy(() => import("./pages/admin/AdminMFASetup"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));

const queryClient = new QueryClient();

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Index />} />
                {/* Common typo/short-link redirects (e.g. LinkedIn "/In") */}
                <Route path="/In" element={<Navigate to="/" replace />} />
                <Route path="/in" element={<Navigate to="/" replace />} />
                <Route path="/about" element={<About />} />
                <Route path="/episodes" element={<Episodes />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/apply" element={<Apply />} />
                <Route path="/apply/success" element={<ApplySuccess />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/texas-entrepreneur-podcast" element={<TexasEntrepreneurPodcast />} />
                <Route path="/podcast/texas-entrepreneur-podcast" element={<TexasEntrepreneurPodcast />} />
                <Route path="/episodes/:id" element={<EpisodeDetail />} />
                <Route path="/press" element={<Press />} />
                <Route path="/checkin" element={<Checkin />} />
                <Route path="/portal/:token" element={<GuestPortal />} />
                <Route path="/guest-signin" element={<GuestSignIn />} />
                <Route path="/guest-reset" element={<GuestReset />} />
                <Route path="/share/:token" element={<SharePack />} />
                {/* Topic Landing Pages */}
                <Route path="/marketing-tips" element={<MarketingTips />} />
                <Route path="/scaling-your-business" element={<ScalingBusiness />} />
                <Route path="/hiring-advice" element={<HiringAdvice />} />
                <Route path="/how-to-start-a-business" element={<HowToStartABusiness />} />
                {/* City Landing Pages */}
                <Route path="/houston-entrepreneur-podcast" element={<HoustonEntrepreneurPodcast />} />
                <Route path="/dallas-entrepreneur-podcast" element={<DallasEntrepreneurPodcast />} />
                <Route path="/best-podcast-in-dallas" element={<BestPodcastInDallas />} />
                <Route path="/austin-entrepreneur-podcast" element={<AustinEntrepreneurPodcast />} />
                <Route path="/san-antonio-entrepreneur-podcast" element={<SanAntonioEntrepreneurPodcast />} />
                {/* Admin Routes */}
                <Route path="/admin/auth" element={<AdminAuth />} />
                <Route path="/admin/reset-password" element={<AdminAcceptInvitation />} />
                <Route path="/admin/mfa-setup" element={<AdminMFASetup />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/admin/posts" element={<AdminPosts />} />
                <Route path="/admin/posts/:id" element={<AdminPostEditor />} />
                <Route path="/admin/ai-studio" element={<AdminAIStudio />} />
                <Route path="/admin/calendar" element={<AdminContentCalendar />} />
                <Route path="/admin/team" element={<AdminTeam />} />
                <Route path="/admin/contacts" element={<AdminContacts />} />
                <Route path="/admin/bookings" element={<AdminBookings />} />
                <Route path="/admin/interview/:bookingId" element={<AdminInterview />} />
                <Route path="/admin/settings" element={<AdminSettings />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
